/**
 * sort.ts -- click-to-sort for the bottom-pane tables.
 *
 * WHY A MODULE AND NOT A FEW LINES IN EACH RENDERER
 * ------------------------------------------------
 * Every renderer in `tradepanel.ts` maps a TYPED array to HTML, so the sort
 * belongs on the array, before the map. Sorting the rendered `<tr>` nodes would
 * be undone by the panel's 15 s snapshot re-render, and would compare FORMATTED
 * strings -- `+$12.03 (+13.67%)`, `64,148.3`, `40x` -- which is lexicographic
 * and therefore wrong for every numeric column ("9" after "10").
 *
 * The pure parts live here so a test can pin them: `cmp` (ordering), `nextSort`
 * (the click cycle) and `sortTh` (the header markup + the visible indicator).
 * The STATE lives in `main.ts` next to `tpTab`, which is the panel's existing
 * single source of truth for "what is on screen"; nothing here holds a copy.
 *
 * NULLS
 * -----
 * A missing value is not zero. `protection.sl` is null for an UNPROTECTED
 * position, `utilization_pct` is null when the dex did not report it. A null
 * sorting as 0 would drop an unprotected position into the middle of the SL
 * column, which is the exact misread that costs money. Nulls therefore go LAST
 * in both directions -- they are always at one end, never interleaved.
 */

/** Which column is sorted and which way. `null` = the array's natural order. */
export type SortState = { key: string; dir: 'asc' | 'desc' } | null;

/** The value a column sorts on. Always the TYPED field, never the rendered text. */
export type SortValue = number | string | null | undefined;

/** Per-table map from a `data-sort` key to the typed field it sorts on. */
export type SortKeys<T> = Record<string, (row: T) => SortValue>;

/**
 * Order two typed values. Nulls last regardless of direction; numbers compared
 * numerically; strings compared as strings.
 */
export function cmp(a: SortValue, b: SortValue, dir: 'asc' | 'desc'): number {
  const aNil = a === null || a === undefined || (typeof a === 'number' && Number.isNaN(a));
  const bNil = b === null || b === undefined || (typeof b === 'number' && Number.isNaN(b));
  // Nulls are NOT ordered by `dir` -- flipping the direction must not walk them
  // through the middle of the column.
  if (aNil || bNil) return aNil && bNil ? 0 : aNil ? 1 : -1;
  const sign = dir === 'asc' ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return (a - b) * sign;
  return String(a).localeCompare(String(b)) * sign;
}

/**
 * A COPY of `rows` in sort order. Copied because `snap.positions` is read by
 * `levelsFor` and the ticket, and a render must not reorder the snapshot itself.
 * Unknown key or no state = natural order, unchanged.
 */
export function sortRows<T>(rows: readonly T[], state: SortState, keys: SortKeys<T>): T[] {
  const pick = state && keys[state.key];
  if (!state || !pick) return [...rows];
  // Array.prototype.sort is stable, so equal values keep their natural order.
  return [...rows].sort((x, y) => cmp(pick(x), pick(y), state.dir));
}

/**
 * The click cycle: ASC -> DESC -> NATURAL.
 *
 * The third click returns to the natural order on purpose. The backend's order
 * carries information no column does -- Open Orders arrives with each bracket
 * leg directly under its parent (the `child` rows with the tree glyph), and any
 * sort breaks that grouping. Without a third click the only way back would be a
 * page reload, so "off" has to be reachable from the same header.
 */
export function nextSort(cur: SortState, key: string): SortState {
  if (!cur || cur.key !== key) return { key, dir: 'asc' };
  if (cur.dir === 'asc') return { key, dir: 'desc' };
  return null;
}

/**
 * A sortable `<th>`. Carries `data-sort` for the delegated click handler in
 * `main.ts` (same `dataset` convention as the overlay checkboxes), plus BOTH
 * `aria-sort` and a visible arrow -- a silently sorted table is one Andrei would
 * misread while sizing a position.
 */
export function sortTh(label: string, key: string, state: SortState, cls = ''): string {
  const on = state?.key === key;
  const aria = on ? (state.dir === 'asc' ? 'ascending' : 'descending') : 'none';
  const arrow = on ? `<span class="sort-arrow">${state.dir === 'asc' ? '\u25b2' : '\u25bc'}</span>` : '';
  const classes = ['sortable', on ? 'sorted' : '', cls].filter(Boolean).join(' ');
  return `<th class="${classes}" data-sort="${key}" aria-sort="${aria}" title="Sort by ${label}">${label}${arrow}</th>`;
}
